import { TypedDocumentNode, gql } from '@apollo/client';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM, LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import { detailedScores } from '@sorare/core/src/components/collections/DetailedScoreLine';
import Dialog from '@sorare/core/src/components/dialog';

import { AllStepsProgressBar } from '../AllStepsProgressBar';
import { DetailedScoreLine } from './DetailedScoreLine';
import { DetailsDialog_cardCollection } from './__generated__/index.graphql';
import books from './assets/books.png';

const Root = styled(Vertical).attrs({ gap: 3 })`
  padding: 0 var(--triple-unit) var(--triple-unit);
`;
const Illustration = styled.img`
  height: 80px;
`;
const WarningWrapper = styled(Horizontal)`
  align-items: flex-start;
  b {
    color: var(--c-yellow-800);
  }
`;
const WarningIconWrapper = styled.div`
  color: var(--c-yellow-600);
`;
const Separator = styled.div`
  height: 1px;
  background: var(--c-nd-150);
`;

type Props = {
  onClose: () => void;
  open: boolean;
  cardCollection: DetailsDialog_cardCollection;
  score: number;
};
export const DetailsDialog = ({
  onClose,
  open,
  cardCollection,
  score,
}: Props) => {
  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      onClose={onClose}
      title={
        <LabelS color="var(--c-nd-600)" bold>
          <FormattedMessage
            id="collections.DetailsDialog.title"
            defaultMessage="The Collection Game"
          />
        </LabelS>
      }
    >
      <Root>
        <Vertical gap={2} center>
          <Illustration src={books} />
          <Vertical gap={0} className="text-center">
            <HeadlineM>
              <FormattedMessage
                id="collections.DetailsDialog.scoringMatrix"
                defaultMessage="Scoring Matrix"
              />
            </HeadlineM>
            <LabelL color="var(--c-nd-600)">
              <FormattedMessage
                id="collections.DetailsDialog.section1.description"
                defaultMessage="Each card has a collection score based on the following"
              />
            </LabelL>
          </Vertical>
        </Vertical>
        <Vertical gap={2}>
          {Object.entries(detailedScores).map(([key, value]) => {
            return <DetailedScoreLine key={key} {...value} />;
          })}
        </Vertical>

        <WarningWrapper>
          <WarningIconWrapper>
            <FontAwesomeIcon icon={faCircleExclamation} />
          </WarningIconWrapper>
          <span>
            <FormattedMessage
              id="collections.Warning.message2"
              defaultMessage="If a card is <b>listed on the market</b> the points do not count in the collection score"
              values={{ b: Bold }}
            />
          </span>
        </WarningWrapper>
        <Separator />
        <Vertical gap={1.5}>
          <LabelS bold color="var(--c-nd-600)">
            <FormattedMessage
              id="collections.DetailsDialog.progress"
              defaultMessage="Current Progress"
            />
          </LabelS>
          <AllStepsProgressBar cardCollection={cardCollection} score={score} />
        </Vertical>
      </Root>
    </Dialog>
  );
};

DetailsDialog.fragments = {
  cardCollection: gql`
    fragment DetailsDialog_cardCollection on CardCollection {
      slug
      ...AllStepsProgressBar_cardCollection
    }
    ${AllStepsProgressBar.fragments.cardCollection}
  ` as TypedDocumentNode<DetailsDialog_cardCollection>,
};
